/*!
 * DH Commerce OS v1.4
 * dh-commerce-shipping.js
 *
 * Modular Shipping Intelligence
 *
 * Responsibilities:
 * - Wait for Shopify/Liquid regional bootstrap configuration
 * - Read Near / Middle / Far state rules
 * - Resolve logged-in customer state when available
 * - Resolve anonymous visitor state using IP location
 * - Maintain DHCommerce.state.shipping
 * - Emit shipping:tier and shipping:location
 * - Support Homepage V2 regional pack visibility
 */

(function (window, document) {
  "use strict";

  var ROOT = window.DHCommerce = window.DHCommerce || {};
  var VERSION = "1.4.1";

  var SHIPPING_EVENT = "shipping:tier";
  var LOCATION_EVENT = "shipping:location";

  var LOCATION_CACHE_KEY =
    "dhcommerce_shipping_location_v2";

  var LOCATION_CACHE_LIFETIME =
    6 * 60 * 60 * 1000;

  var BOOTSTRAP_WAIT_INTERVAL = 100;
  var BOOTSTRAP_WAIT_LIMIT = 100;

  var started = false;
  var starting = false;
  var startPromise = null;


  /* ======================================================
     BASE STATE
  ====================================================== */

  ROOT.state = ROOT.state || {};

  ROOT.state.shipping =
    ROOT.state.shipping || {};

  function ensureState() {
    var state = ROOT.state.shipping;

    if (!state.tier) {
      state.tier = "unknown";
    }

    if (!state.effectiveTier) {
      state.effectiveTier = "far";
    }

    if (!state.source) {
      state.source = "default";
    }

    if (!state.confidence) {
      state.confidence = "unknown";
    }

    if (!state.location) {
      state.location = {
        country: "",
        state: "",
        city: "",
        zip: ""
      };
    }

    if (typeof state.initialized !== "boolean") {
      state.initialized = false;
    }

    return state;
  }

  ensureState();


  /* ======================================================
     SHIPPING API
  ====================================================== */

  var shipping =
    ROOT.shipping = ROOT.shipping || {};

  shipping.name = "shipping";
  shipping.version = VERSION;

  shipping.validTiers = [
    "near",
    "middle",
    "far",
    "unknown"
  ];


  /* ======================================================
     HELPERS
  ====================================================== */

  function normalizeString(value) {
    return String(value || "").trim();
  }

  function normalizeState(value) {
    return normalizeString(value).toUpperCase();
  }

  function normalizeTier(value) {
    var tier =
      normalizeString(value).toLowerCase();

    return shipping.validTiers.indexOf(tier) !== -1
      ? tier
      : "unknown";
  }

  function effectiveTier(value) {
    var tier = normalizeTier(value);

    return tier === "unknown"
      ? "far"
      : tier;
  }

  function parseStateList(value) {
    if (Array.isArray(value)) {
      return value
        .map(normalizeState)
        .filter(Boolean);
    }

    return String(value || "")
      .split(",")
      .map(normalizeState)
      .filter(Boolean);
  }

  shipping.normalizeTier = normalizeTier;
  shipping.getEffectiveTier = effectiveTier;
  shipping.parseStateList = parseStateList;


  /* ======================================================
     EVENT SYSTEM
  ====================================================== */

  function dispatchNative(name, payload) {
    /*
     * Direct event used by Homepage V2.
     */
    try {
      document.dispatchEvent(
        new CustomEvent(name, {
          detail: payload
        })
      );
    } catch (error) {
      console.warn(
        "[DH Commerce Shipping] Could not dispatch " +
          name,
        error
      );
    }
  }

  function emit(name, payload) {
    payload = payload || {};

    /*
     * Always dispatch the direct browser event.
     *
     * This is intentionally NOT mutually exclusive with
     * the modular event bus. Homepage V2 may listen
     * directly for "shipping:tier".
     */
    dispatchNative(name, payload);

    /*
     * Modular DH Commerce event bus.
     */
    if (
      ROOT.events &&
      typeof ROOT.events.emit === "function"
    ) {
      try {
        ROOT.events.emit(name, payload);
      } catch (error) {
        console.warn(
          "[DH Commerce Shipping] Event bus error:",
          error
        );
      }
    }

    /*
     * Legacy DH Commerce compatibility event.
     */
    try {
      document.dispatchEvent(
        new CustomEvent("dhcommerce", {
          detail: {
            name: name,
            payload: payload,
            frameworkVersion: VERSION,
            timestamp: Date.now()
          }
        })
      );
    } catch (error) {
      /* Non-fatal compatibility failure. */
    }
  }


  /* ======================================================
     BOOTSTRAP
  ====================================================== */

  shipping.getBootstrap = function () {
    if (
      window.DHCommerceBootstrap &&
      typeof window.DHCommerceBootstrap === "object"
    ) {
      return window.DHCommerceBootstrap;
    }

    return {};
  };


  shipping.bootstrapReady = function () {
    var bootstrap =
      shipping.getBootstrap();

    return !!(
      bootstrap.regionalRules &&
      typeof bootstrap.regionalRules === "object"
    );
  };


  shipping.waitForBootstrap = function () {
    if (shipping.bootstrapReady()) {
      return Promise.resolve(
        shipping.getBootstrap()
      );
    }

    return new Promise(function (resolve) {
      var attempts = 0;

      var timer = window.setInterval(
        function () {
          attempts += 1;

          if (shipping.bootstrapReady()) {
            window.clearInterval(timer);

            resolve(
              shipping.getBootstrap()
            );

            return;
          }

          if (
            attempts >= BOOTSTRAP_WAIT_LIMIT
          ) {
            window.clearInterval(timer);

            console.warn(
              "[DH Commerce Shipping] Regional bootstrap was not found after waiting."
            );

            resolve(
              shipping.getBootstrap()
            );
          }
        },
        BOOTSTRAP_WAIT_INTERVAL
      );
    });
  };


  /* ======================================================
     REGIONAL RULES
  ====================================================== */

  shipping.getRegionalRules = function () {
    var bootstrap =
      shipping.getBootstrap();

    var source =
      bootstrap.regionalRules &&
      typeof bootstrap.regionalRules === "object"
        ? bootstrap.regionalRules
        : {};

    var defaultTier =
      normalizeTier(
        source.defaultTier || "far"
      );

    if (defaultTier === "unknown") {
      defaultTier = "far";
    }

    return {
      active:
        source.active === true,

      key:
        normalizeString(
          source.key || "us_standard"
        ),

      nearStates:
        parseStateList(
          source.nearStates
        ),

      middleStates:
        parseStateList(
          source.middleStates
        ),

      farStates:
        parseStateList(
          source.farStates
        ),

      excludedRegions:
        parseStateList(
          source.excludedRegions
        ),

      defaultTier:
        defaultTier
    };
  };


  shipping.resolveTierFromState =
    function (stateCode) {
      var state =
        normalizeState(stateCode);

      var rules =
        shipping.getRegionalRules();

      if (!state) {
        return {
          tier: rules.defaultTier,
          matched: false,
          excluded: false,
          state: ""
        };
      }

      if (!rules.active) {
        return {
          tier: rules.defaultTier,
          matched: false,
          excluded: false,
          state: state
        };
      }

      if (
        rules.excludedRegions.indexOf(
          state
        ) !== -1
      ) {
        return {
          tier: rules.defaultTier,
          matched: false,
          excluded: true,
          state: state
        };
      }

      if (
        rules.nearStates.indexOf(
          state
        ) !== -1
      ) {
        return {
          tier: "near",
          matched: true,
          excluded: false,
          state: state
        };
      }

      if (
        rules.middleStates.indexOf(
          state
        ) !== -1
      ) {
        return {
          tier: "middle",
          matched: true,
          excluded: false,
          state: state
        };
      }

      if (
        rules.farStates.indexOf(
          state
        ) !== -1
      ) {
        return {
          tier: "far",
          matched: true,
          excluded: false,
          state: state
        };
      }

      return {
        tier: rules.defaultTier,
        matched: false,
        excluded: false,
        state: state
      };
    };


  /* ======================================================
     THRESHOLDS
  ====================================================== */

  shipping.getRules = function (tier) {
    var resolvedTier =
      effectiveTier(
        tier ||
        ensureState().effectiveTier
      );

    var defaults = {
      near: {
        freeShippingThreshold: 59,
        giftThreshold: 69
      },

      middle: {
        freeShippingThreshold: 79,
        giftThreshold: 89
      },

      far: {
        freeShippingThreshold: 99,
        giftThreshold: 109
      }
    };

    if (
      ROOT.config &&
      typeof ROOT.config.get === "function"
    ) {
      var configured =
        ROOT.config.get(
          "shipping." + resolvedTier,
          null
        );

      if (configured) {
        return configured;
      }
    }

    return (
      defaults[resolvedTier] ||
      defaults.far
    );
  };


  /* ======================================================
     LOCATION
  ====================================================== */

  shipping.setLocation =
    function (location) {
      location = location || {};

      var state = ensureState();

      state.location = {
        country:
          normalizeState(
            location.country
          ),

        state:
          normalizeState(
            location.state
          ),

        city:
          normalizeString(
            location.city
          ),

        zip:
          normalizeString(
            location.zip
          )
      };

      return state.location;
    };


  shipping.getCustomerLocation =
    function () {
      var bootstrap =
        shipping.getBootstrap();

      var source =
        bootstrap.customerLocation &&
        typeof bootstrap.customerLocation ===
          "object"
          ? bootstrap.customerLocation
          : {};

      return {
        loggedIn:
          source.loggedIn === true,

        country:
          normalizeState(
            source.country
          ),

        state:
          normalizeState(
            source.state
          ),

        city:
          normalizeString(
            source.city
          ),

        zip:
          normalizeString(
            source.zip
          )
      };
    };


  /* ======================================================
     CACHE
  ====================================================== */

  shipping.saveLocationCache =
    function (tier, location) {
      try {
        window.localStorage.setItem(
          LOCATION_CACHE_KEY,
          JSON.stringify({
            tier:
              normalizeTier(tier),

            location: {
              country:
                normalizeState(
                  location.country
                ),

              state:
                normalizeState(
                  location.state
                ),

              city:
                normalizeString(
                  location.city
                ),

              zip:
                normalizeString(
                  location.zip
                )
            },

            savedAt: Date.now()
          })
        );
      } catch (error) {
        /* Storage may be unavailable. */
      }
    };


  shipping.getLocationCache =
    function () {
      try {
        var raw =
          window.localStorage.getItem(
            LOCATION_CACHE_KEY
          );

        if (!raw) {
          return null;
        }

        var cached =
          JSON.parse(raw);

        if (
          !cached ||
          !cached.savedAt ||
          Date.now() -
            Number(cached.savedAt) >
            LOCATION_CACHE_LIFETIME
        ) {
          window.localStorage.removeItem(
            LOCATION_CACHE_KEY
          );

          return null;
        }

        if (
          !cached.location ||
          !cached.location.state
        ) {
          return null;
        }

        var tier =
          normalizeTier(cached.tier);

        if (tier === "unknown") {
          return null;
        }

        return {
          tier: tier,

          location: {
            country:
              normalizeState(
                cached.location.country
              ),

            state:
              normalizeState(
                cached.location.state
              ),

            city:
              normalizeString(
                cached.location.city
              ),

            zip:
              normalizeString(
                cached.location.zip
              )
          },

          savedAt:
            Number(cached.savedAt)
        };
      } catch (error) {
        return null;
      }
    };


  /* ======================================================
     SESSION
  ====================================================== */

  shipping.saveSessionTier =
    function (tier) {
      try {
        window.sessionStorage.setItem(
          "dhcommerce_shipping_tier",
          normalizeTier(tier)
        );
      } catch (error) {
        /* Non-fatal. */
      }
    };


  shipping.getSessionTier =
    function () {
      try {
        var tier =
          window.sessionStorage.getItem(
            "dhcommerce_shipping_tier"
          );

        if (!tier) {
          return null;
        }

        tier = normalizeTier(tier);

        return tier === "unknown"
          ? null
          : tier;
      } catch (error) {
        return null;
      }
    };


  /* ======================================================
     PREVIEW / MANUAL TESTING
  ====================================================== */

  shipping.getPreviewOverride =
    function () {
      try {
        var params =
          new URLSearchParams(
            window.location.search
          );

        var tier =
          params.get("dh_shipping");

        if (!tier) {
          return null;
        }

        tier =
          normalizeTier(tier);

        return tier === "unknown"
          ? null
          : tier;
      } catch (error) {
        return null;
      }
    };


  /* ======================================================
     SET TIER
  ====================================================== */

  shipping.setTier =
    function (tier, options) {
      options = options || {};

      var state = ensureState();

      var normalizedTier =
        normalizeTier(tier);

      var resolvedEffectiveTier =
        effectiveTier(
          normalizedTier
        );

      state.tier =
        normalizedTier;

      state.effectiveTier =
        resolvedEffectiveTier;

      state.source =
        options.source ||
        "manual";

      state.confidence =
        options.confidence ||
        "unknown";

      if (options.persist !== false) {
        shipping.saveSessionTier(
          normalizedTier
        );
      }

      var payload = {
        tier:
          normalizedTier,

        effectiveTier:
          resolvedEffectiveTier,

        source:
          state.source,

        confidence:
          state.confidence,

        location:
          state.location,

        rules:
          shipping.getRules(
            resolvedEffectiveTier
          )
      };

      /*
       * IMPORTANT:
       * Homepage V2 needs this exact event after
       * effectiveTier has been written.
       */
      emit(
        SHIPPING_EVENT,
        payload
      );

      /*
       * Generic state-change compatibility.
       */
      try {
        document.dispatchEvent(
          new CustomEvent(
            "dhcommerce:state-changed",
            {
              detail: {
                state: ROOT.state
              }
            }
          )
        );
      } catch (error) {
        /* Non-fatal. */
      }

      return payload;
    };


  /* ======================================================
     IP GEOLOCATION
  ====================================================== */

  shipping.getAnonymousLocation =
    function () {
      var controller =
        typeof AbortController !==
        "undefined"
          ? new AbortController()
          : null;

      var timeout =
        window.setTimeout(
          function () {
            if (controller) {
              controller.abort();
            }
          },
          5000
        );

      return fetch(
        "https://ipapi.co/json/",
        {
          method: "GET",

          headers: {
            Accept: "application/json"
          },

          signal:
            controller
              ? controller.signal
              : undefined
        }
      )
        .then(function (response) {
          if (!response.ok) {
            throw new Error(
              "IP location request returned " +
                response.status
            );
          }

          return response.json();
        })
        .then(function (data) {
          window.clearTimeout(
            timeout
          );

          return {
            country:
              normalizeState(
                data.country_code
              ),

            state:
              normalizeState(
                data.region_code
              ),

            city:
              normalizeString(
                data.city
              ),

            zip:
              normalizeString(
                data.postal
              )
          };
        })
        .catch(function (error) {
          window.clearTimeout(
            timeout
          );

          console.warn(
            "[DH Commerce Shipping] IP location lookup failed:",
            error
          );

          return null;
        });
    };


  shipping.resolveAnonymousVisitor =
    function () {
      return shipping
        .getAnonymousLocation()
        .then(function (location) {
          if (!location) {
            return null;
          }

          if (
            location.country !== "US"
          ) {
            return null;
          }

          if (!location.state) {
            return null;
          }

          var resolution =
            shipping.resolveTierFromState(
              location.state
            );

          /*
           * Even if the state isn't explicitly matched,
           * apply the configured fallback tier.
           */
          shipping.setLocation(
            location
          );

          shipping.saveLocationCache(
            resolution.tier,
            location
          );

          var payload =
            shipping.setTier(
              resolution.tier,
              {
                source:
                  resolution.matched
                    ? "ip_state"
                    : "ip_state_fallback",

                confidence:
                  resolution.matched
                    ? "estimated"
                    : "fallback",

                persist: true
              }
            );

          emit(
            LOCATION_EVENT,
            {
              tier:
                resolution.tier,

              matched:
                resolution.matched,

              excluded:
                resolution.excluded,

              source:
                payload.source,

              confidence:
                payload.confidence,

              location:
                ensureState().location
            }
          );

          return payload;
        });
    };


  /* ======================================================
     INITIAL RESOLUTION
  ====================================================== */

  function resolveInitialTier() {
    var previewTier =
      shipping.getPreviewOverride();

    var customer =
      shipping.getCustomerLocation();

    var cache =
      shipping.getLocationCache();

    var sessionTier =
      shipping.getSessionTier();


    /*
     * 1. Explicit preview override.
     */
    if (previewTier) {
      shipping.setTier(
        previewTier,
        {
          source:
            "preview_override",

          confidence:
            "testing",

          persist: false
        }
      );

      return Promise.resolve(
        ensureState()
      );
    }


    /*
     * 2. Logged-in Shopify customer address.
     */
    if (
      customer.loggedIn &&
      customer.state
    ) {
      var customerResolution =
        shipping.resolveTierFromState(
          customer.state
        );

      shipping.setLocation(
        customer
      );

      shipping.saveLocationCache(
        customerResolution.tier,
        customer
      );

      shipping.setTier(
        customerResolution.tier,
        {
          source:
            customerResolution.matched
              ? "shopify_customer_state"
              : "shopify_customer_fallback",

          confidence:
            customerResolution.matched
              ? "verified_customer_address"
              : "fallback",

          persist: true
        }
      );

      return Promise.resolve(
        ensureState()
      );
    }


    /*
     * 3. Recent location cache.
     *
     * We still refresh anonymous location afterward,
     * because a shopper may have traveled.
     */
    if (cache) {
      shipping.setLocation(
        cache.location
      );

      shipping.setTier(
        cache.tier,
        {
          source:
            "location_cache",

          confidence:
            "recent_ip_estimate",

          persist: true
        }
      );

      return shipping
        .resolveAnonymousVisitor()
        .then(function () {
          return ensureState();
        });
    }


    /*
     * 4. Session tier.
     *
     * Use it temporarily, then refresh via IP.
     */
    if (sessionTier) {
      shipping.setTier(
        sessionTier,
        {
          source:
            "session",

          confidence:
            "previously_assigned",

          persist: true
        }
      );

      return shipping
        .resolveAnonymousVisitor()
        .then(function () {
          return ensureState();
        });
    }


    /*
     * 5. Unknown visitor.
     *
     * Immediately establish safe Far fallback so the
     * 4-count cannot flash for unresolved visitors.
     */
    shipping.setTier(
      "unknown",
      {
        source: "default",
        confidence: "unknown",
        persist: false
      }
    );


    /*
     * Then resolve the anonymous visitor.
     */
    return shipping
      .resolveAnonymousVisitor()
      .then(function () {
        return ensureState();
      });
  }


  /* ======================================================
     START
  ====================================================== */

  shipping.init = function () {
    if (started) {
      return Promise.resolve(
        ensureState()
      );
    }

    if (starting && startPromise) {
      return startPromise;
    }

    starting = true;

    startPromise =
      shipping
        .waitForBootstrap()
        .then(function () {
          var rules =
            shipping.getRegionalRules();

          console.log(
            "[DH Commerce Shipping] Regional rules loaded:",
            rules
          );

          if (!rules.active) {
            console.warn(
              "[DH Commerce Shipping] Regional rules are inactive."
            );
          }

          return resolveInitialTier();
        })
        .then(function (state) {
          state.initialized = true;

          started = true;
          starting = false;

          emit(
            "shipping:ready",
            {
              tier:
                state.tier,

              effectiveTier:
                state.effectiveTier,

              source:
                state.source,

              confidence:
                state.confidence,

              location:
                state.location,

              regionalRules:
                shipping.getRegionalRules()
            }
          );

          console.log(
            "[DH Commerce Shipping] READY",
            {
              tier:
                state.tier,

              effectiveTier:
                state.effectiveTier,

              source:
                state.source,

              location:
                state.location
            }
          );

          return state;
        })
        .catch(function (error) {
          starting = false;

          console.error(
            "[DH Commerce Shipping] Initialization failed:",
            error
          );

          /*
           * Preserve safe Far behavior on failure.
           */
          shipping.setTier(
            "unknown",
            {
              source:
                "initialization_error",

              confidence:
                "unknown",

              persist: false
            }
          );

          ensureState().initialized =
            true;

          return ensureState();
        });

    return startPromise;
  };


  shipping.refresh = function () {
    /*
     * Explicitly ignore cache/session and rerun IP.
     * Useful from dashboard or browser console.
     */
    return shipping
      .waitForBootstrap()
      .then(function () {
        return shipping
          .resolveAnonymousVisitor();
      });
  };


  /* ======================================================
     HEALTH / DIAGNOSTICS
  ====================================================== */

  shipping.getHealth = function () {
    var state =
      ensureState();

    var rules =
      shipping.getRegionalRules();

    return {
      name:
        "shipping",

      version:
        VERSION,

      initialized:
        !!state.initialized,

      healthy:
        !!state.initialized,

      status:
        state.initialized
          ? "ready"
          : "waiting",

      tier:
        state.tier,

      effectiveTier:
        state.effectiveTier,

      source:
        state.source,

      confidence:
        state.confidence,

      country:
        state.location.country,

      state:
        state.location.state,

      city:
        state.location.city,

      zip:
        state.location.zip,

      regionalRulesActive:
        rules.active,

      regionalRulesKey:
        rules.key,

      nearStates:
        rules.nearStates,

      middleStates:
        rules.middleStates,

      farStates:
        rules.farStates,

      excludedRegions:
        rules.excludedRegions,

      defaultTier:
        rules.defaultTier
    };
  };


  shipping.diagnostics =
    function () {
      return {
        version:
          VERSION,

        bootstrapFound:
          shipping.bootstrapReady(),

        bootstrap:
          shipping.getBootstrap(),

        regionalRules:
          shipping.getRegionalRules(),

        shippingState:
          ensureState(),

        health:
          shipping.getHealth()
      };
    };


  /* ======================================================
     REGISTER ENGINE
  ====================================================== */

  if (
    ROOT.engines &&
    typeof ROOT.engines.register ===
      "function"
  ) {
    try {
      ROOT.engines.register(
        "shipping",
        shipping,
        {
          version:
            VERSION,

          source:
            "dh-commerce-shipping.js"
        }
      );
    } catch (error) {
      console.warn(
        "[DH Commerce Shipping] Engine registration skipped:",
        error
      );
    }
  }


  /* ======================================================
     AUTO START
  ====================================================== */

  /*
   * Do NOT wait solely for DOMContentLoaded.
   *
   * Homepage V2 creates DHCommerceBootstrap from Liquid.
   * waitForBootstrap() guarantees the regional settings
   * actually exist before resolution begins.
   */

  function autoStart() {
    shipping.init();
  }

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      autoStart,
      { once: true }
    );
  } else {
    window.setTimeout(
      autoStart,
      0
    );
  }

})(window, document);